import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Loader,
  CloudUpload,
  Link2,
  Mic,
  Chrome,
  MonitorPlay,
} from 'lucide-react';
import { AppStoreBadge, GooglePlayBadge } from './common';
import { getIntentFromUrl } from './intentTranslations';
import { getIntentConfig, getIntentCategoryFromParam } from './intentConfig';
import { DEMO_COMPONENTS } from './demo';
import './translations/i18n';

// Loading component with a nice animation
export const LoadingComponent = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
      <Loader className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
    </div>
  );
};

// Error boundary component
interface ErrorBoundaryProps {
  children: React.ReactNode;
  t: (key: string) => string;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  // eslint-disable-next-line react/static-property-placement
  static propTypes: {
    children: PropTypes.Validator<NonNullable<PropTypes.ReactNodeLike>>;
    t: PropTypes.Validator<(...args: any[]) => any>;
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    const { children, t } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        <div className="p-6 text-center">
          <h3 className="text-lg font-semibold text-destructive mb-2">
            {t('common.somethingWentWrong')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('common.tryRefreshing')}
          </p>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  t: PropTypes.func.isRequired,
};

interface Tool {
  key: string;
  name: string;
  description: string;
  icon?: React.ComponentType<any>;
}

interface MatchingTool {
  key: string;
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
}

// Lazy load all tool components
const TOOLS: {
  [key: string]: React.LazyExoticComponent<React.ComponentType<any>>;
} = {
  smartnotepad: lazy(() => import('./SmartNotePad')),
  upload: lazy(() => import('./Upload')),
  importurl: lazy(() => import('./ImportURL')),
  audiorecorder: lazy(() => import('./AudioRecorder')),
  screenrecorder: lazy(() => import('./ScreenRecorder')),
  transcriber: lazy(() => import('./LiveTranscriber')),
  texttospeech: lazy(() => import('./TextToSpeech')),
  tabaudiorecorder: lazy(() => import('./TabAudioRecorder')),
  pdftoaudio: lazy(() => import('./PDFToAudio')),
  imagechat: lazy(() => import('./ImageChat')),
  aihumanizer: lazy(() => import('./AIHumanizer')),
  audiocutter: lazy(() => import('./AudioCutter')),
  voicetester: lazy(() => import('./VoiceTester')),
  dictationapp: lazy(() => import('./DictationApp')),
  voicetoapp: lazy(() => import('./VoiceToApp')),
  youtubesummarizer: lazy(() => import('./YouTubeSummarizer')),
  translator: lazy(() => import('./LiveTranslator')),
  audiotoinvoice: lazy(() => import('./AudioToInvoice')),
  p2pmeeting: lazy(() => import('./P2PMeeting')),
  askyoutube: lazy(() => import('./YouTubeSummarizer')),
  webcammictester: lazy(() => import('./WebcamMicTester')),
  texttosrt: lazy(() => import('./TextToSRT')),
  pdftobrainrot: lazy(() => import('./PDFToBrainrot')),
  screentranslator: lazy(() => import('./ScreenTranslator')),
  audiosummarizer: lazy(() => import('./AudioSummarizer')),
  youtubedescriptiongenerator: lazy(
    () => import('./YouTubeDescriptionGenerator'),
  ),
  meetingminutesgenerator: lazy(() => import('./MeetingMinutesGenerator')),
  addtochrome: lazy(() => import('./AddToChrome')),
  geminitranscriber: lazy(() => import('./GeminiTranscriber')),
  mobileappcta: lazy(() => import('./MobileAppCTA')),
  youtubetranscription: lazy(() => import('./YouTubeTranscription')),
};

const TOOL_INFO: Tool[] = [
  {
    key: 'mobileappcta',
    name: 'tools.mobileappcta.name',
    description: 'tools.mobileappcta.description',
  },
  {
    key: 'smartnotepad',
    name: 'tools.smartnotepad.name',
    description: 'tools.smartnotepad.description',
  },
  {
    key: 'upload',
    name: 'tools.upload.name',
    description: 'tools.upload.description',
    icon: CloudUpload,
  },
  {
    key: 'importurl',
    name: 'tools.importurl.name',
    description: 'tools.importurl.description',
    icon: Link2,
  },
  {
    key: 'audiorecorder',
    name: 'tools.audiorecorder.name',
    description: 'tools.audiorecorder.description',
    icon: Mic,
  },
  {
    key: 'screenrecorder',
    name: 'tools.screenrecorder.name',
    description: 'tools.screenrecorder.description',
    icon: MonitorPlay,
  },
  {
    key: 'transcriber',
    name: 'tools.transcriber.name',
    description: 'tools.transcriber.description',
  },
  {
    key: 'texttospeech',
    name: 'tools.texttospeech.name',
    description: 'tools.texttospeech.description',
  },
  {
    key: 'tabaudiorecorder',
    name: 'tools.tabaudiorecorder.name',
    description: 'tools.tabaudiorecorder.description',
  },
  {
    key: 'pdftoaudio',
    name: 'tools.pdftoaudio.name',
    description: 'tools.pdftoaudio.description',
  },
  {
    key: 'imagechat',
    name: 'tools.imagechat.name',
    description: 'tools.imagechat.description',
  },
  {
    key: 'aihumanizer',
    name: 'tools.aihumanizer.name',
    description: 'tools.aihumanizer.description',
  },
  {
    key: 'audiocutter',
    name: 'tools.audiocutter.name',
    description: 'tools.audiocutter.description',
  },
  {
    key: 'voicetester',
    name: 'tools.voicetester.name',
    description: 'tools.voicetester.description',
  },
  {
    key: 'dictationapp',
    name: 'tools.dictationapp.name',
    description: 'tools.dictationapp.description',
  },
  {
    key: 'voicetoapp',
    name: 'tools.voicetoapp.name',
    description: 'tools.voicetoapp.description',
  },
  {
    key: 'youtubesummarizer',
    name: 'tools.youtubesummarizer.name',
    description: 'tools.youtubesummarizer.description',
  },
  {
    key: 'translator',
    name: 'tools.translator.name',
    description: 'tools.translator.description',
  },
  {
    key: 'audiotoinvoice',
    name: 'tools.audiotoinvoice.name',
    description: 'tools.audiotoinvoice.description',
  },
  {
    key: 'p2pmeeting',
    name: 'tools.p2pmeeting.name',
    description: 'tools.p2pmeeting.description',
  },
  {
    key: 'askyoutube',
    name: 'tools.askyoutube.name',
    description: 'tools.askyoutube.description',
  },
  {
    key: 'webcammictester',
    name: 'tools.webcammictester.name',
    description: 'tools.webcammictester.description',
  },
  {
    key: 'texttosrt',
    name: 'tools.texttosrt.name',
    description: 'tools.texttosrt.description',
  },
  {
    key: 'pdftobrainrot',
    name: 'tools.pdfToBrainrot.name',
    description: 'tools.pdfToBrainrot.description',
  },
  {
    key: 'screentranslator',
    name: 'tools.screentranslator.name',
    description: 'tools.screentranslator.description',
  },
  {
    key: 'audiosummarizer',
    name: 'tools.audiosummarizer.name',
    description: 'tools.audiosummarizer.description',
  },
  {
    key: 'youtubedescriptiongenerator',
    name: 'tools.youtubedescriptiongenerator.name',
    description: 'tools.youtubedescriptiongenerator.description',
  },
  {
    key: 'meetingminutesgenerator',
    name: 'tools.meetingminutesgenerator.name',
    description: 'tools.meetingminutesgenerator.description',
  },
  {
    key: 'addtochrome',
    name: 'tools.addtochrome.name',
    description: 'tools.addtochrome.description',
    icon: Chrome,
  },
  {
    key: 'geminitranscriber',
    name: 'tools.geminitranscriber.name',
    description: 'tools.geminitranscriber.description',
  },
  {
    key: 'youtubetranscription',
    name: 'tools.youtubetranscription.name',
    description: 'tools.youtubetranscription.description',
  },
];

const BASE_MOBILE_URL = 'https://screenapp.io/mobile';

const MicroTool: React.FC = () => {
  const { t } = useTranslation();
  const { toolName } = useParams<{ toolName: string }>();
  const toolKeys = toolName?.toLowerCase().split(',') || [];
  const [isLoading, setIsLoading] = useState(true);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Device detection
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));

    if (!toolName) {
      document.title = 'Micro Tools | ScreenApp';
      setIsLoading(false);
      return;
    }

    const matchingTool = TOOL_INFO.find(
      (tool) => tool.key.toLowerCase() === toolKeys[0]?.trim().toLowerCase(),
    );

    if (matchingTool) {
      document.title = `${t(matchingTool.name)} | ScreenApp`;
    } else {
      document.title = 'Tool Not Found | ScreenApp';
    }

    setIsLoading(false);
  }, [toolName, toolKeys, t]);

  // Get the mobile URL with intent parameter if it exists
  const getMobileUrl = () => {
    const hashPart = window.location.hash.split('?')[1] || '';
    const urlParams = new URLSearchParams(hashPart);
    const intent = urlParams.get('intent') || '';

    const url = new URL(BASE_MOBILE_URL);
    url.searchParams.append('utm_source', intent || 'website');
    url.searchParams.append('utm_medium', 'microtools');
    url.searchParams.append('utm_campaign', 'combination_tools');

    return url.toString();
  };

  // Helper component to render intent demo
  const renderIntentDemo = () => {
    const intent = getIntentFromUrl();
    if (!intent) return null;

    // Get the intent category first, then find the config
    const intentCategory = getIntentCategoryFromParam(intent);
    if (!intentCategory) return null;

    const intentConfig = getIntentConfig(intentCategory);
    if (!intentConfig || !intentConfig.demoComponent) return null;

    const DemoComponent =
      DEMO_COMPONENTS[
        intentConfig.demoComponent as keyof typeof DEMO_COMPONENTS
      ];
    if (!DemoComponent) return null;

    return (
      <div className="mt-8">
        <ErrorBoundary t={t}>
          <Suspense fallback={<LoadingComponent />}>
            <DemoComponent />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  };

  // Helper component to render mobile-first hierarchy with vertical layout
  const renderMobileFirstLayout = (tools: MatchingTool[]) => {
    if (!(isIOS || isAndroid)) {
      // Desktop: show all tools vertically with "or" separator
      return (
        <div>
          <div className="space-y-8">
            {tools.map(({ key, Component }, index) => (
              <div key={key}>
                <ErrorBoundary t={t}>
                  <Suspense fallback={<LoadingComponent />}>
                    <Component showIOSBadge={false} />
                  </Suspense>
                </ErrorBoundary>
                {index < tools.length - 1 && (
                  <div className="flex items-center justify-center my-8">
                    <span className="text-gray-500 font-medium">
                      {t('common.or')}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Render intent demo below tools */}
          {renderIntentDemo()}
        </div>
      );
    }

    // Mobile: filter out mobileappcta from tools and show vertical layout
    const webTools = tools.filter(({ key }) => key !== 'mobileappcta');

    // If no web tools, just show mobile app install buttons
    if (webTools.length === 0) {
      // If both iOS and Android, show tabs
      if (isIOS && isAndroid) {
        return (
          <div className="flex justify-center py-8">
            <Tabs defaultValue="ios" className="w-full max-w-md">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="ios">iOS</TabsTrigger>
                <TabsTrigger value="android">Android</TabsTrigger>
              </TabsList>
              <TabsContent value="ios" className="flex justify-center pt-4">
                <AppStoreBadge
                  mobileUrl={getMobileUrl()}
                  showLabel={false}
                  height={48}
                />
              </TabsContent>
              <TabsContent value="android" className="flex justify-center pt-4">
                <GooglePlayBadge
                  mobileUrl={getMobileUrl()}
                  showLabel={false}
                  height={48}
                />
              </TabsContent>
            </Tabs>
          </div>
        );
      }

      // Single platform
      return (
        <div className="flex justify-center py-8">
          <div className="flex flex-col items-center gap-4">
            {isIOS && (
              <AppStoreBadge
                mobileUrl={getMobileUrl()}
                showLabel={false}
                height={48}
              />
            )}

            {isAndroid && (
              <GooglePlayBadge
                mobileUrl={getMobileUrl()}
                showLabel={false}
                height={48}
              />
            )}
          </div>
        </div>
      );
    }

    // Mobile with web tools: use tabs with install as first tab
    const tabsList = [
      { value: 'install', label: t('tools.mobileappcta.installApp') },
      ...webTools.map(({ key }) => ({
        value: key,
        label: t(TOOL_INFO.find((tool) => tool.key === key)?.name || ''),
      })),
    ];

    return (
      <div>
        <Tabs defaultValue="install" className="w-full">
          <div className="flex justify-center">
            <TabsList className="inline-flex">
              {tabsList.map(({ value, label }) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Install Tab Content */}
          <TabsContent value="install" className="mt-6">
            {isIOS && isAndroid ? (
              <div className="flex justify-center py-8">
                <Tabs defaultValue="ios" className="w-full max-w-md">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="ios">iOS</TabsTrigger>
                    <TabsTrigger value="android">Android</TabsTrigger>
                  </TabsList>
                  <TabsContent value="ios" className="flex justify-center pt-4">
                    <AppStoreBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={48}
                    />
                  </TabsContent>
                  <TabsContent
                    value="android"
                    className="flex justify-center pt-4"
                  >
                    <GooglePlayBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={48}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="flex justify-center py-8">
                <div className="flex flex-col items-center gap-4">
                  {isIOS && (
                    <AppStoreBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={48}
                    />
                  )}

                  {isAndroid && (
                    <GooglePlayBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={48}
                    />
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Web Tool Tabs */}
          {webTools.map(({ key, Component }) => (
            <TabsContent key={key} value={key} className="mt-6">
              <ErrorBoundary t={t}>
                <Suspense fallback={<LoadingComponent />}>
                  <Component showIOSBadge={false} />
                </Suspense>
              </ErrorBoundary>
            </TabsContent>
          ))}
        </Tabs>

        {/* Render intent demo below tools */}
        {renderIntentDemo()}
      </div>
    );
  };

  if (!toolName) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold mb-6">
          {t('common.availableTools')}
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {TOOL_INFO.map((tool) => (
            <Link
              key={tool.key}
              to={`/tool/${tool.key}`}
              className="block h-full"
              aria-label={`${t(tool.name)} - ${t(tool.description)}`}
            >
              <Card className="h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{t(tool.name)}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t(tool.description)}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Find matching tools
  const matchingTools = toolKeys
    .map((toolKey) => {
      const matchingKey = Object.keys(TOOLS).find(
        (key) => key.toLowerCase() === toolKey.trim().toLowerCase(),
      );
      return matchingKey
        ? { key: matchingKey, Component: TOOLS[matchingKey] }
        : null;
    })
    .filter((tool): tool is MatchingTool => tool !== null);

  if (matchingTools.length === 0) {
    // Default to upload and importurl when no valid tools found
    const defaultTools = ['upload', 'importurl']
      .map((toolKey) => ({
        key: toolKey,
        Component: TOOLS[toolKey],
      }))
      .filter((tool): tool is MatchingTool => tool !== null);

    if (defaultTools.length > 0) {
      return (
        <div className="py-12 px-2 sm:px-4 lg:px-8">
          {renderMobileFirstLayout(defaultTools)}
        </div>
      );
    }

    return <div>{t('common.noValidTools')}</div>;
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="py-12 px-2 sm:px-4 lg:px-8">
      {renderMobileFirstLayout(matchingTools)}
    </div>
  );
};

export default MicroTool;
